import { useEffect, useState } from "react";
import { FolderSimple, Check, Trash, Plus, Minus } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { toastTrashed } from "@/lib/trashToast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const plural = (n) => `${n} recipe${n === 1 ? "" : "s"}`;
const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;
const btnPrimary = "w-full mt-2 px-5 py-3 rounded-full bg-rf-orange text-white font-bold disabled:opacity-50 hover:bg-rf-orange/90 transition-colors";

function Shell({ open, onOpenChange, title, desc, testid, children }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-[calc(100%-1.5rem)] rounded-2xl" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
          {desc && <DialogDescription>{desc}</DialogDescription>}
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

export function MoveDialog({ open, onOpenChange, ids, excludeCookbookId, onDone }) {
  const [books, setBooks] = useState(null);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setTarget("");
    api.get("/cookbooks").then((r) => setBooks((r.data.owned || []).filter((c) => c.cookbook_id !== excludeCookbookId))).catch(() => setBooks([]));
  }, [open, excludeCookbookId]);
  const move = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/recipes/bulk-move", { recipe_ids: ids, target_cookbook_id: target });
      toast.success(`Moved ${plural(data.moved)} to ${data.title}`);
      onDone({ ...data, ids });
    } catch (e) { toast.error(errMsg(e, "Couldn't move those recipes")); }
    setBusy(false);
  };
  return (
    <Shell open={open} onOpenChange={onOpenChange} title={`Move ${plural(ids.length)}`} desc="Choose one of your cookbooks." testid="bulk-move-dialog">
      <div className="max-h-[50vh] overflow-y-auto -mx-1 px-1 space-y-2" data-testid="bulk-move-cookbook-list">
        {books === null && <p className="text-sm text-rf-ink-soft">Loading your cookbooks…</p>}
        {books?.length === 0 && <p className="text-sm text-rf-ink-soft">You don't have another cookbook yet. Create one from your dashboard first.</p>}
        {books?.map((c) => (
          <button key={c.cookbook_id} type="button" onClick={() => setTarget(c.cookbook_id)} data-testid={`bulk-move-target-${c.cookbook_id}`}
                  className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-semibold flex items-center gap-3 transition-colors ${target === c.cookbook_id ? "border-rf-orange bg-rf-amber/20" : "border-rf-ink/10 hover:bg-rf-cream"}`}>
            <FolderSimple size={18} className="text-rf-orange shrink-0" />
            <span className="truncate flex-1">{c.title}</span>
            {target === c.cookbook_id && <Check size={16} weight="bold" className="text-rf-orange" />}
          </button>
        ))}
      </div>
      <button type="button" disabled={!target || busy} onClick={move} data-testid="bulk-move-confirm-btn" className={btnPrimary}>
        {busy ? "Moving…" : `Move ${plural(ids.length)}`}
      </button>
    </Shell>
  );
}

export function TagDialog({ open, onOpenChange, ids, selectedTags, onDone }) {
  const [mode, setMode] = useState("add");
  const [tag, setTag] = useState("");
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setTag("");
    api.get("/me/recipe-tags").then((r) => setMine(r.data.tags || [])).catch(() => setMine([]));
  }, [open]);
  const pool = mode === "add" ? mine : selectedTags;
  const q = tag.trim().toLowerCase();
  const suggestions = pool.filter((t) => !q || t.toLowerCase().includes(q)).slice(0, 12);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/recipes/bulk-tags", { recipe_ids: ids, [mode]: [tag.trim()] });
      toast.success(mode === "add" ? `Added "${tag.trim()}" to ${plural(data.changed)}` : `Removed "${tag.trim()}" from ${plural(data.changed)}`);
      onDone(data);
    } catch (e) { toast.error(errMsg(e, "Couldn't update tags")); }
    setBusy(false);
  };
  return (
    <Shell open={open} onOpenChange={onOpenChange} title={`Edit tags on ${plural(ids.length)}`} testid="bulk-tag-dialog">
      <div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-rf-cream-alt" role="tablist">
        {[["add", "Add a tag", Plus], ["remove", "Remove a tag", Minus]].map(([m, label, Icon]) => (
          <button key={m} type="button" onClick={() => { setMode(m); setTag(""); }} data-testid={`bulk-tag-mode-${m}`} aria-pressed={mode === m}
                  className={`inline-flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-bold transition-colors ${mode === m ? "bg-white shadow text-rf-ink" : "text-rf-ink-soft"}`}>
            <Icon size={14} weight="bold" /> {label}
          </button>
        ))}
      </div>
      <input value={tag} onChange={(e) => setTag(e.target.value)} maxLength={40} autoFocus data-testid="bulk-tag-input"
             onKeyDown={(e) => { if (e.key === "Enter" && tag.trim() && !busy) save(); }}
             placeholder={mode === "add" ? "Type a tag, e.g. Weeknight" : "Which tag should go?"}
             className="w-full rounded-xl border border-rf-ink/15 px-4 py-3 text-sm focus:outline-none focus:border-rf-orange" />
      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="bulk-tag-suggestions">
          {suggestions.map((t) => (
            <button key={t} type="button" onClick={() => setTag(t)} data-testid={`bulk-tag-suggestion-${t}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${tag === t ? "bg-rf-amber text-rf-ink" : "bg-rf-cream-alt text-rf-ink-soft hover:bg-rf-amber/30"}`}>{t}</button>
          ))}
        </div>
      )}
      {mode === "remove" && selectedTags.length === 0 && <p className="text-sm text-rf-ink-soft">The selected recipes don't have any tags yet.</p>}
      <button type="button" disabled={!tag.trim() || busy} onClick={save} data-testid="bulk-tag-confirm-btn" className={btnPrimary}>
        {busy ? "Saving…" : mode === "add" ? `Add to ${plural(ids.length)}` : `Remove from ${plural(ids.length)}`}
      </button>
    </Shell>
  );
}

export function TrashDialog({ open, onOpenChange, ids, onDone }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/recipes/bulk-trash", { recipe_ids: ids });
      toastTrashed(data.ids);
      onDone(data);
    } catch (e) { toast.error(errMsg(e, "Couldn't delete those recipes")); }
    setBusy(false);
  };
  return (
    <Shell open={open} onOpenChange={onOpenChange} title={`Move ${plural(ids.length)} to Trash?`} testid="bulk-trash-dialog"
           desc="You can restore them from Trash on your dashboard for 30 days. After that they're deleted for good.">
      <div className="flex flex-col sm:flex-row gap-2 mt-2">
        <button type="button" onClick={() => onOpenChange(false)} data-testid="bulk-trash-cancel-btn" className="btn-outline-ink justify-center flex-1">Keep them</button>
        <button type="button" disabled={busy} onClick={go} data-testid="bulk-trash-confirm-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold disabled:opacity-50 transition-colors">
          <Trash size={16} /> {busy ? "Deleting…" : "Move to Trash"}
        </button>
      </div>
    </Shell>
  );
}
